import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { KeyValue } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, resource } from "@angular/core";

import { GetAirtScansCountQuery, SearchFilter, Severity } from "../../../../apis/networkdashboard/graphql/graphql";
import { AIRT_SCAN_COUNT_QUERY } from "../../../../apis/networkdashboard/static/graphql-queries/airtscan";
import { ProductType } from "../../../../helpers/enums/productType";
import { reportAndReturn } from "../../../../helpers/report-to-sentry";
import { AuthService } from "../../../../services/authservice";
import { DashboardTileComponent } from "../dashboard-tile/dashboard-tile";

@Component({
  selector: "surfcert-container",
  template: `<dashboard-tile [subscription]="certTile()" [customerActivities]="[]" />`,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DashboardTileComponent],
})
export class SurfcertContainer {
  private readonly apollo = inject(Apollo);
  private readonly auth = inject(AuthService);

  private readonly securityScans = resource({
    params: () => this.auth.viewingCustomer().customerId,
    loader: ({ params: customerId }) => {
      const beforeSourceDate = new Date();
      const afterSourceDate = new Date();
      afterSourceDate.setDate(afterSourceDate.getDate() - 7);
      const searchFilter: SearchFilter = {
        severity: [Severity.Critical, Severity.High, Severity.Medium],
        beforeSourceDate,
        afterSourceDate,
        customerId,
      };

      return firstValueFrom(
        this.apollo.query<GetAirtScansCountQuery>({
          query: AIRT_SCAN_COUNT_QUERY,
          variables: { filter: searchFilter, first: 99 },
        }),
      )
        .then(({ data }) =>
          data?.airtScans?.edges.reduce(
            (acc, item) => {
              const key = item.node.severity?.toLowerCase();
              if (key) acc[key] = (acc[key] || 0) + 1;
              return acc;
            },
            { medium: 0, high: 0, critical: 0 },
          ),
        )
        .catch(reportAndReturn({ medium: 0, high: 0, critical: 0 }));
    },
  });

  protected readonly certTile = computed<KeyValue<ProductType, any>>(() => ({
    key: ProductType.SURFcert,
    value: {
      available: false,
      loading: false,
      status: {
        "active-corero-filters": 0,
        "inactive-corero-filters": 2,
        "open-incidents": 0,
      },
      productType: ProductType.SURFcert,
      key: "cert",
      url: "cert",
      subscriptions: 0,
      subscriptionsIds: [],
      messages: [],
      actionButtonText: "Global.SeeOverview",
      counts: this.securityScans.value(),
    },
  }));
}
